import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  ArrowRightLeft,
  Bell,
  BookOpen,
  Building2,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FileText,
  KeyRound,
  Lightbulb,
  Link2,
  Mail,
  Receipt,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
  Wallet,
  Webhook,
  MessageSquare,
} from "lucide-react";

export interface IntegrationInfoListItem {
  icon: LucideIcon;
  title: string;
  description?: string;
}

export interface IntegrationInfoSection {
  id: string;
  heading: string;
  icon: LucideIcon;
  accentClass: string;
  paragraphs?: string[];
  items?: IntegrationInfoListItem[];
}

export interface IntegrationInfoContent {
  id: "quickbooks" | "stripe" | "clickconnector";
  title: string;
  summary: string;
  logoSrc: string;
  headerClassName: string;
  iconRingClassName: string;
  sections: IntegrationInfoSection[];
}

export const QUICKBOOKS_INTEGRATION_INFO: IntegrationInfoContent = {
  id: "quickbooks",
  title: "QuickBooks",
  summary:
    "Connect QuickBooks Online to automatically push booking accounting from TA Desk into your QuickBooks company.",
  logoSrc: "/assets/integrations/qb-logo.png",
  headerClassName:
    "bg-gradient-to-br from-[#2CA01C]/10 via-emerald-500/5 to-background",
  iconRingClassName: "ring-emerald-500/20 bg-white",
  sections: [
    {
      id: "overview",
      heading: "What it does",
      icon: Sparkles,
      accentClass: "text-emerald-600 dark:text-emerald-400",
      paragraphs: [
        "When connected and sync is enabled, TA Desk sends accounting events to QuickBooks as they happen in your bookings workflow. Sync is one-way — data flows from TA Desk to QuickBooks, not the other way around.",
      ],
    },
    {
      id: "synced",
      heading: "What gets synced",
      icon: ArrowRightLeft,
      accentClass: "text-blue-600 dark:text-blue-400",
      items: [
        {
          icon: FileText,
          title: "Customer invoices",
          description: "Created and updated when pricing or payment schedules change",
        },
        {
          icon: Receipt,
          title: "Customer payment receipts",
          description: "Recorded when payments are received; voided when reversed",
        },
        {
          icon: Truck,
          title: "Supplier bills",
          description: "Created and updated when supplier costs or schedules change",
        },
        {
          icon: Wallet,
          title: "Supplier bill payments",
          description: "Recorded when you pay suppliers; voided when reversed",
        },
        {
          icon: Users,
          title: "Contacts & suppliers",
          description: "Matched or created as QuickBooks Customers and Vendors",
        },
        {
          icon: CreditCard,
          title: "PDQ-reconciled payments",
          description: "Pushed after batch card reconciliation completes",
        },
      ],
    },
    {
      id: "setup",
      heading: "Setup requirements",
      icon: Settings2,
      accentClass: "text-violet-600 dark:text-violet-400",
      items: [
        {
          icon: Link2,
          title: "Authorize via OAuth",
          description: "Opens QuickBooks in a new tab to grant access",
        },
        {
          icon: BookOpen,
          title: "Default income account",
          description: "Used for customer invoices posted to QuickBooks",
        },
        {
          icon: Building2,
          title: "Default expense account",
          description: "Used for supplier bills posted to QuickBooks",
        },
        {
          icon: RefreshCw,
          title: "Enable automatic sync",
          description: "Turn on once both default accounts are selected",
        },
      ],
    },
    {
      id: "tips",
      heading: "Good to know",
      icon: Lightbulb,
      accentClass: "text-amber-600 dark:text-amber-400",
      items: [
        {
          icon: CheckCircle2,
          title: "Runs in the background",
          description: "Sync starts after each booking transaction completes",
        },
        {
          icon: AlertCircle,
          title: "Retry on failure",
          description: "Sync errors are saved on the transaction for later retry",
        },
        {
          icon: ShieldCheck,
          title: "Safe to disconnect",
          description: "Disconnecting removes the link but keeps existing QuickBooks data",
        },
      ],
    },
  ],
};

export const STRIPE_INTEGRATION_INFO: IntegrationInfoContent = {
  id: "stripe",
  title: "Stripe",
  summary:
    "Accept quote deposits and booking payments online via Stripe Checkout, with payments recorded automatically in TA Desk.",
  logoSrc: "/assets/integrations/stripe-logo.png",
  headerClassName:
    "bg-gradient-to-br from-[#635BFF]/12 via-indigo-500/5 to-background",
  iconRingClassName: "ring-indigo-500/20 bg-white",
  sections: [
    {
      id: "overview",
      heading: "What it does",
      icon: Sparkles,
      accentClass: "text-indigo-600 dark:text-indigo-400",
      paragraphs: [
        "Stripe lets your customers pay deposits on quotes and instalments or balances on bookings through secure Stripe Checkout pages. When a payment succeeds, TA Desk records a customer payment receipt and can notify your team.",
      ],
    },
    {
      id: "payments",
      heading: "Payment types supported",
      icon: CreditCard,
      accentClass: "text-violet-600 dark:text-violet-400",
      items: [
        {
          icon: FileText,
          title: "Quote deposits",
          description: "Customers pay the required deposit from the public quote page",
        },
        {
          icon: CalendarClock,
          title: "Booking instalments",
          description: "Scheduled tranche payments from payment plans",
        },
        {
          icon: Wallet,
          title: "Booking balance",
          description: "Remaining amount due on a confirmed booking",
        },
        {
          icon: Mail,
          title: "Email pay buttons",
          description: "Optional Pay now links in reminder and confirmation emails",
        },
      ],
    },
    {
      id: "after-payment",
      heading: "What happens after payment",
      icon: Webhook,
      accentClass: "text-blue-600 dark:text-blue-400",
      items: [
        {
          icon: Webhook,
          title: "Webhook notification",
          description: "Stripe notifies TA Desk when checkout completes",
        },
        {
          icon: Receipt,
          title: "Receipt created",
          description: "A customer payment receipt is posted to the booking or quote",
        },
        {
          icon: CreditCard,
          title: "Processing fees",
          description: "Stripe fees auto-recorded when PDQ charges are enabled",
        },
        {
          icon: Bell,
          title: "Agent notifications",
          description: "Your team can be alerted when a customer pays via email link",
        },
      ],
    },
    {
      id: "setup",
      heading: "Setup requirements",
      icon: Settings2,
      accentClass: "text-emerald-600 dark:text-emerald-400",
      items: [
        {
          icon: KeyRound,
          title: "Restricted API key",
          description: "Customers (Read), Checkout Sessions (Write), Webhook Endpoints (Write)",
        },
        {
          icon: Link2,
          title: "Connect in workspace",
          description: "TA Desk registers payment webhooks automatically",
        },
        {
          icon: Building2,
          title: "Brand payment methods",
          description: "One customer payment method per brand, matching home currency",
        },
        {
          icon: Mail,
          title: "Email pay buttons",
          description: "Optionally enable per brand on reminder and confirmation templates",
        },
      ],
    },
    {
      id: "tips",
      heading: "Good to know",
      icon: Lightbulb,
      accentClass: "text-amber-600 dark:text-amber-400",
      items: [
        {
          icon: ShieldCheck,
          title: "Use a restricted key",
          description: "Prefer rk_live_… or rk_test_… over your full secret key",
        },
        {
          icon: Building2,
          title: "One mapping per brand",
          description: "Receipts post to the correct ledger for each brand",
        },
        {
          icon: AlertCircle,
          title: "Disconnecting",
          description: "Removes the integration and deactivates payment webhooks",
        },
      ],
    },
  ],
};

export const CLICKCONNECTOR_INTEGRATION_INFO: IntegrationInfoContent = {
  id: "clickconnector",
  title: "ClickConnector",
  summary:
    "Link a ClickConnector workspace to each brand so agents can store a conversation ID on enquiries and quotes.",
  logoSrc: "/assets/integrations/clickconnect-logo.svg",
  headerClassName:
    "bg-gradient-to-br from-[#0166FF]/12 via-sky-500/5 to-background",
  iconRingClassName: "ring-sky-500/20 bg-white",
  sections: [
    {
      id: "overview",
      heading: "What it does",
      icon: Sparkles,
      accentClass: "text-sky-600 dark:text-sky-400",
      paragraphs: [
        "Connect one ClickConnector workspace to each TA Desk brand using an organization domain and API token. When a brand is connected, Quick Add Quote and Add Enquiry show a ClickConnector conversation ID field.",
      ],
    },
    {
      id: "setup",
      heading: "Setup",
      icon: Settings2,
      accentClass: "text-blue-600 dark:text-blue-400",
      items: [
        {
          icon: Building2,
          title: "Connect per brand",
          description:
            "Each brand can link one ClickConnector workspace in Integrations",
        },
        {
          icon: KeyRound,
          title: "Organization domain + API token",
          description:
            "Saved on the brand. Tokens are stored securely and not shown again",
        },
        {
          icon: MessageSquare,
          title: "Conversation ID on enquiries",
          description:
            "Optional field next to tags, shown when the selected brand is connected",
        },
      ],
    },
    {
      id: "tips",
      heading: "Good to know",
      icon: Lightbulb,
      accentClass: "text-amber-600 dark:text-amber-400",
      items: [
        {
          icon: Link2,
          title: "One workspace per brand",
          description: "A brand can only connect a single ClickConnector inbox",
        },
        {
          icon: ShieldCheck,
          title: "Rotating a token",
          description:
            "Leave the token blank to keep the existing one, or paste a new token to replace it",
        },
      ],
    },
  ],
};
